import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import Button, { type ButtonProps } from "./Button";

export interface RefreshButtonProps extends Omit<ButtonProps, "onClick"> {
  onClick: (event: MouseEvent<HTMLButtonElement>) => unknown | Promise<unknown>;
}

/** Only an explicit click animates this button; query polling never changes it. */
export default function RefreshButton({
  onClick,
  loading,
  icon = "refresh",
  ...props
}: RefreshButtonProps) {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const finish = () => {
    inFlight.current = false;
    if (mounted.current) setPending(false);
  };
  return (
    <Button
      {...props}
      icon={icon}
      loading={pending || loading}
      onClick={(event) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setPending(true);
        try {
          // Error rendering belongs to the query/mutation; always release the control.
          void Promise.resolve(onClick(event)).then(finish, finish);
        } catch (error) {
          finish();
          throw error;
        }
      }}
    />
  );
}
